(() => {
  if (window.__mjrMotion) return;
  window.__mjrMotion = true;
  const html = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const editor = Boolean(window.Shopify?.designMode);
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  let observer, mutationObserver, progress, frame = 0;
  let enabled = false, strength = .6, images = [], copies = [], fills = [], bands = [];
  const revealed = new WeakSet();
  const states = new WeakMap();
  function reveal(el) {
    revealed.add(el);
    el.classList.add('mjr-sr-in');
    observer?.unobserve(el);
  }
  function update() {
    frame = 0;
    if (!enabled) return;
    const vh = window.innerHeight;
    let moving = false;
    if (progress) {
      const max = html.scrollHeight - vh;
      html.style.setProperty('--mjr-scroll', max > 0 ? clamp(window.scrollY / max, 0, 1) : 0);
    }
    for (const image of images) {
      const rect = image.parentElement.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > vh) continue;
      const target = clamp((vh / 2 - rect.top - rect.height / 2) * .1 * strength, -rect.height * .04, rect.height * .04);
      const previous = states.get(image) ?? target;
      const next = Math.abs(target - previous) < .1 ? target : previous + (target - previous) * .18;
      states.set(image, next);
      image.style.setProperty('--mjr-py', `${next.toFixed(2)}px`);
      if (Math.abs(target - next) >= .1) moving = true;
    }
    for (const copy of copies) {
      const rect = copy.closest('.mjr-hero').getBoundingClientRect();
      const p = clamp(-rect.top / rect.height, 0, 1) * strength;
      copy.style.setProperty('--mjr-copy-o', (1 - p * .4).toFixed(3));
      copy.style.setProperty('--mjr-copy-y', `${(-p * 35).toFixed(2)}px`);
    }
    for (const block of fills) {
      const rect = block.el.getBoundingClientRect();
      const p = clamp((vh * .85 - rect.top) / (rect.height + vh * .2), 0, 1);
      const count = Math.round(p * block.words.length);
      if (block.count === count) continue;
      block.count = count;
      block.words.forEach((word, index) => word.classList.toggle('is-lit', index < count));
    }
    for (const band of bands) {
      const rect = band.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > vh) continue;
      band.style.setProperty('--mjr-band', clamp((vh - rect.top) / (vh + rect.height), 0, 1).toFixed(4));
    }
    if (moving) schedule();
  }
  function schedule() { if (!frame && enabled) frame = requestAnimationFrame(update); }
  function init() {
    const config = document.querySelector('[data-mjr-config]');
    enabled = !editor && !reduce.matches && config?.dataset.enabled !== 'false';
    strength = clamp(Number(config?.dataset.strength ?? 60) / 100, 0, 1);
    html.classList.toggle('mjr-motion', enabled);
    html.style.setProperty('--mjr-reveal-duration', `${Number(config?.dataset.duration ?? 900)}ms`);
    html.style.setProperty('--mjr-sr-distance', `${18 + strength * 24}px`);
    observer?.disconnect();
    images = Array.from(document.querySelectorAll('main .mjr-hero__media, main .hero__media-grid, main .mjr-feature__media, main .mj-hero-photo, main .mj-founder-photo, main .mjc-hero-photo')).flatMap(el => {
      el.classList.add('mjr-image-window');
      return Array.from(el.querySelectorAll('img'));
    });
    images.forEach(image => { image.classList.toggle('mjr-parallax', enabled && strength > 0); });
    copies = Array.from(document.querySelectorAll('main .mjr-hero__copy'));
    copies.forEach(copy => copy.classList.toggle('mjr-fade-out', enabled));
    fills = Array.from(document.querySelectorAll('[data-mjr-fill]')).map(el => ({ el, words: Array.from(el.querySelectorAll('.mjr-word')), count: -1 }));
    bands = Array.from(document.querySelectorAll('[data-mjr-band]'));
    if (!enabled) {
      document.querySelectorAll('.mjr-sr').forEach(reveal);
      fills.forEach(block => block.words.forEach(word => word.classList.add('is-lit')));
      bands.forEach(band => band.style.setProperty('--mjr-band', .5));
      progress?.remove(); progress = null;
      if (frame) cancelAnimationFrame(frame); frame = 0;
      return;
    }
    if ('IntersectionObserver' in window) {
      observer ??= new IntersectionObserver(entries => entries.forEach(entry => { if (entry.isIntersecting) reveal(entry.target); }), { rootMargin: '0px 0px -6% 0px', threshold: 0 });
      const candidates = document.querySelectorAll('main > .shopify-section, main .mj-section, main .mjc-section');
      candidates.forEach(el => {
        if (el.matches('main > .shopify-section') && (el.querySelector('.mj-section, .mjc-section, product-form-component, .product-information, .main-cart, .facets') || el === document.querySelector('main > .shopify-section'))) return;
        el.classList.add('mjr-sr');
        Array.from(el.querySelectorAll('.mj-value, .mj-journey-card, .mjc-value, .mjc-product, .mjc-journey-card, product-card, .collection-card')).forEach((card, index) => {
          card.classList.add('mjr-stagger');
          card.style.setProperty('--mjr-i', Math.min(index, 4));
        });
        const rect = el.getBoundingClientRect();
        if (revealed.has(el) || rect.top < window.innerHeight * .94) reveal(el);
        else observer.observe(el);
      });
    }
    if (config?.dataset.progress !== 'false') {
      if (!progress) { progress = document.createElement('div'); progress.className = 'mjr-progress'; progress.setAttribute('aria-hidden', 'true'); document.body.appendChild(progress); }
    } else { progress?.remove(); progress = null; }
    schedule();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  reduce.addEventListener('change', init);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', init);
  document.addEventListener('focusin', event => { const el = event.target.closest?.('.mjr-sr'); if (el) reveal(el); });
  document.addEventListener('shopify:block:select', event => { const el = event.target.closest?.('.mjr-sr'); if (el) reveal(el); });
  const main = document.querySelector('main');
  if (main && 'MutationObserver' in window) {
    mutationObserver = new MutationObserver(records => { if (records.some(record => record.type === 'childList')) init(); });
    mutationObserver.observe(main, { childList: true, subtree: true });
  }
})();
